import Autocomplete from '@betterfunnel/ui-core/Autocomplete';
import ColorPickerField from '@betterfunnel/ui-core/ColorPickerField';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import Slider from '@betterfunnel/ui-core/Slider';
import { toCSS, toJSON } from 'css-convert-json';
import { useContext, useEffect, useState } from 'react';
import FileUploadInput from '../components/FormFields/FileUpload/FileUploadInput';
import { DashboardContext } from '../contexts/DashboardContext';
import { isValidCss } from '../utils/validator';

export const ALLOWED_FONTS = [
  'Roboto',
  'Poppins',
  'Open Sans',
  'Montserrat',
  'Raleway',
  'Inter',
  'Lexend',
  'Rubik',
];

export default function Brand() {
  const { accountBrand, updateAccountBrand } = useContext(DashboardContext);
  const [buttonCSS, setButtonCSS] = useState<string>('');

  useEffect(() => {
    if (!accountBrand?.buttonCSS) return;
    if (buttonCSS) return;

    setButtonCSS(toCSS(accountBrand.buttonCSS as any).trim());
  }, [accountBrand?.buttonCSS, buttonCSS]);

  return (
    <div className="py-8">
      <div className="pb-8 sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Brand
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Add your brand to your landing pages.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Logo
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-500">Add your logo.</p>

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Logo
            </dt>
            <div className="w-full max-w-[415px]">
              <FileUploadInput
                image={accountBrand?.logo}
                onSelected={(image) => {
                  updateAccountBrand({ logo: image || (null as any) });
                }}
              />
            </div>
          </div>
        </dl>
      </div>

      <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Color Pallet
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Primary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={accountBrand?.primaryColor}
                  debounce
                  onChange={(v) => updateAccountBrand({ primaryColor: v })}
                />
              </dd>
            </div>
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Secondary Color
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <ColorPickerField
                  value={accountBrand?.secondaryColor}
                  debounce
                  onChange={(v) => updateAccountBrand({ secondaryColor: v })}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Buttons
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom button styling to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Custom Button CSS
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <TextArea
                  value={buttonCSS}
                  validation={{
                    status: !isValidCss(buttonCSS) ? 'error' : undefined,
                    text: !isValidCss(buttonCSS) ? 'Invalid CSS' : undefined,
                  }}
                  onChange={(e) => {
                    const newButtonCSS = e.target.value;
                    setButtonCSS(newButtonCSS);

                    if (isValidCss(newButtonCSS)) {
                      console.log(
                        'updated!!!',
                        newButtonCSS,
                        toJSON(newButtonCSS),
                      );
                      updateAccountBrand({ buttonCSS: toJSON(newButtonCSS) });
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Font
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom font to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Font
              </dt>
              <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <Autocomplete
                  selected={accountBrand?.font ?? 'Inter'}
                  options={ALLOWED_FONTS}
                  onChange={(selected) => {
                    updateAccountBrand({
                      font: selected as any,
                    });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Border Radius
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom border radius to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Border radius
              </dt>
              <dd className="mt-1 flex items-center gap-x-6 sm:mt-0 sm:flex-auto">
                <Slider
                  min={0}
                  max={54}
                  step={0.5}
                  value={accountBrand?.borderRadius ?? 6}
                  onChange={(e) => {
                    updateAccountBrand({
                      borderRadius: e.target.valueAsNumber,
                    });
                  }}
                />
                <TextField
                  className="h-16 max-w-[64px] text-center"
                  style={{ borderRadius: accountBrand?.borderRadius ?? 6 }}
                  value={`${accountBrand?.borderRadius ?? 6}`}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    try {
                      const num = parseFloat(newVal || '0');
                      updateAccountBrand({
                        borderRadius: num,
                      });
                    } catch (_) {
                      /* empty */
                    }
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Shadow
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Add your custom shadow to your landing pages.
          </p>

          <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
            <div className="pt-6 sm:flex">
              <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                Shadow
              </dt>
              <dd className="mt-1 flex items-center justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <Toggle
                  checked={accountBrand?.shadow ?? true}
                  onChange={(checked) => {
                    updateAccountBrand({
                      shadow: checked,
                    });
                  }}
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
